// The site pins' look: a location pin, its round head over a tip that stands
// on the place, drawn in ONE body colour with the tone in a dot in the middle
// of the head. Rasterised here as plain RGBA rather than through a <canvas>,
// so the same pixels reach MapLibre's `addImage` in a browser and in the
// tests (jsdom has no 2-D canvas).
//
// Every length is in CSS pixels at the resting size (icon-size 1); the
// selected size is the layer's `icon-size`, so the shape scales
// as one piece. The legend's SVG pin
// (packs/prospecting/app/views/prospecting/shared/_legend_pin.html.erb) draws
// the same shape, and a test holds it to PIN_SHAPE.

// The head is 16 px across, as the circle the pins used to be: at half that
// the pins were too small to tell apart on the map.
const HEAD_RADIUS = 8
// From the head's centre to the tip.
const TIP_LENGTH = 18
// The tone's dot, in the middle of the head: 10 px of the head's 16, so the
// tone is most of what a rep sees and the body is a ring round it.
const DOT_RADIUS = 5
// A white edge all round, as the old circles had, so a dark body still reads
// on the aerial imagery.
const OUTLINE = 1
const OUTLINE_COLOUR = [255, 255, 255]

// The image is a 32 px square with the pin at its bottom middle and the rest
// transparent. MapLibre hit-tests a symbol by its whole image (plus its own
// 2 px icon padding), so the square is the click target: 32 px resting, never
// smaller than the old circle's 20 (16 inside a 2 px stroke), and 52 px
// selected (×1.625) against the old selected circle's 34.
const BOX = 32

// The numbers above, for the test that holds the legend's SVG pin to them.
export const PIN_SHAPE = Object.freeze({
  headRadius: HEAD_RADIUS,
  tipLength: TIP_LENGTH,
  dotRadius: DOT_RADIUS,
  outline: OUTLINE,
  box: BOX,
})

// "#rrggbb" as [r, g, b], or null for anything else.
export const parseHex = (hex) => {
  const match = /^#([\da-f]{6})$/i.exec(String(hex || "").trim())
  if (!match) return null
  return [0, 2, 4].map((at) => parseInt(match[1].slice(at, at + 2), 16))
}

// Signed distance from a point to the pin's body (negative inside): a circle
// of HEAD_RADIUS joined by tangents to a point TIP_LENGTH below its centre —
// Inigo Quilez's "uneven capsule" with a tip of radius 0. `x` and `y` are
// relative to the head's centre, `y` growing towards the tip.
const pinDistance = (x, y) => {
  const px = Math.abs(x)
  const b = HEAD_RADIUS / TIP_LENGTH
  const a = Math.sqrt(1 - b * b)
  const k = -b * px + a * y
  if (k < 0) return Math.hypot(px, y) - HEAD_RADIUS
  if (k > a * TIP_LENGTH) return Math.hypot(px, y - TIP_LENGTH)
  return a * px + b * y - HEAD_RADIUS
}

const coverage = (distance, pixelRatio) =>
  Math.min(1, Math.max(0, 0.5 - distance * pixelRatio))

const mix = (from, to, amount) =>
  from.map((channel, at) => channel + (to[at] - channel) * amount)

// One pin as {width, height, data} — straight (not premultiplied) RGBA, the
// shape MapLibre's addImage takes — drawn at `pixelRatio` device pixels per
// CSS pixel. `body` and `tone` are [r, g, b].
export const pinImage = ({ body, tone, pixelRatio }) => {
  const size = BOX * pixelRatio
  const data = new Uint8Array(size * size * 4)
  // The outline's round end sits on the image's bottom edge.
  const centreX = BOX / 2
  const centreY = BOX - OUTLINE - TIP_LENGTH

  for (let row = 0; row < size; row++) {
    for (let column = 0; column < size; column++) {
      const x = (column + 0.5) / pixelRatio - centreX
      const y = (row + 0.5) / pixelRatio - centreY
      const distance = pinDistance(x, y)
      const alpha = coverage(distance - OUTLINE, pixelRatio)
      if (alpha === 0) continue

      const inBody = coverage(distance, pixelRatio)
      const inDot = coverage(Math.hypot(x, y) - DOT_RADIUS, pixelRatio)
      const colour = mix(mix(OUTLINE_COLOUR, body, inBody), tone, inDot)
      const at = (row * size + column) * 4
      data[at] = Math.round(colour[0])
      data[at + 1] = Math.round(colour[1])
      data[at + 2] = Math.round(colour[2])
      data[at + 3] = Math.round(alpha * 255)
    }
  }
  return { width: size, height: size, data }
}
